import * as Yup from 'yup';
import { AxiosError, AxiosResponse } from 'axios';
import { debounce } from 'lodash';
import { Logger } from '../Logger';
import { endpointEnum } from '../../services/rest/makeAPIRequest';
import { MutableRefObject } from 'react';
import { UseMutationResult } from 'react-query';

// debounce timeout between after last keypress
export const CHECK_EMAIL_AVAILABILITY_DEBOUNCE_IN_MS = 250;

interface IValidateUniqueEmail {
  /** From Yup context */
  createError: (
    params?:
      | {
          path?: string | undefined;
          message?: string | undefined;
          params?: Record<string, unknown> | undefined;
        }
      | undefined
  ) => Yup.ValidationError;
  /** From Yup context */
  path: string;
  /** Error Message to show user */
  message: string;
  /** Email value */
  value: string;
  /** Promise resolve callback */
  resolve: (
    value:
      | boolean
      | Yup.ValidationError
      | PromiseLike<boolean | Yup.ValidationError>
  ) => void;
  /** Promise reject callback */
  reject: (reason?: any) => void;
  /** Reference used to store previous email/errorMessage */
  previousRef: MutableRefObject<{ email: string; errorMessage: string }>;
  /** API request object from useMutation function */
  mutation: UseMutationResult<AxiosResponse<any>>;
}

/**
 * Validate email (value) and return true or error message (message)
 */
const validateEmailAvailable = async ({
  createError,
  path,
  message,
  value,
  resolve,
  reject,
  previousRef,
  mutation,
}: IValidateUniqueEmail): Promise<any> => {
  // select Yup validation schema
  const schema = Yup.string().email();
  const isEmail = schema.isValidSync(value?.trim());

  // exit early if not valid email
  if (!isEmail || !value) {
    return resolve(
      createError({
        path,
        message: 'invalid email',
      })
    );
  }

  // return cached result if no changes
  if (value === previousRef.current?.email) {
    return resolve(
      previousRef.current?.errorMessage
        ? createError({
            path,
            message: previousRef.current?.errorMessage,
          })
        : true
    );
  } else {
    // otherwise compute result
    try {
      await mutation.mutateAsync({
        endpoint: endpointEnum.CHECK_EMAIL_AVAILABILITY,
        payload: {
          email: value,
        },
      });
    } catch (exception) {
      // if exception occurred show error only if found
      // otherwise log any error and return success as to not block submit
      const axiosException = exception as AxiosError;
      // error if email found
      if (axiosException?.response?.status === 409) {
        // save off email/errorMessage
        previousRef.current = {
          email: value,
          errorMessage: axiosException?.response?.data?.message,
        };

        return resolve(
          createError({
            path,
            message: previousRef.current.errorMessage,
          })
        );
      }

      // on error log message and assume email valid
      Logger.error(
        {
          message:
            axiosException?.response?.data?.message ?? 'Fail emailValidator',
          path,
          exception,
          errorMessage: axiosException?.message,
        },
        'checkEmailAvailability::validateEmailAvailable'
      );
    }

    previousRef.current = {
      email: value,
      errorMessage: '',
    };
    resolve(true);
  }
};

/**
 * Create debouce for validator with CHECK_EMAIL_AVAILABILITY_DEBOUNCE_IN_MS timeout
 * @see https://stackoverflow.com/questions/58576825/yup-formik-async-validation-with-debounce
 */
const validateEmailDebounced = debounce(
  validateEmailAvailable,
  CHECK_EMAIL_AVAILABILITY_DEBOUNCE_IN_MS
);

/**
 * Validation checker for Yup
 * @see https://medium.com/make-it-heady/react-typescript-hooks-form-validation-with-formik-yup-and-material-ui-d4901efc0096
 *
 * @param this Yup caller
 * @param message Message to displlay
 * @param previousRef Reference used to store previous email/errorMessage
 * @param mutation API request object from useMutation function
 */
function checkEmailAvailability(
  this: Yup.StringSchema<string | undefined, Record<string, unknown>>,
  message: string,
  previousRef: MutableRefObject<{ email: string; errorMessage: string }>,
  mutation: UseMutationResult<AxiosResponse<any>>
) {
  return this.test('is-email-unique', message, function (value: any) {
    const { path, createError } = this;

    return new Promise((resolve, reject) =>
      validateEmailDebounced({
        createError,
        path,
        message,
        value,
        resolve,
        reject,
        previousRef,
        mutation,
      })
    );
  });
}

/**
 * Write custom validator for Yup checking for email address already in use
 * with debounce CHECK_EMAIL_AVAILABILITY_DEBOUNCE_IN_MS timeout
 *
 * @example
 *  import '../../../../../common/formik-material-ui/checkEmailAvailability';
 *
 *  const QueueEditSchema = Yup.object({
 *    submitQueueEditPayload: Yup.object({
 *      email: Yup.string().checkEmailAvailability('This Email Address is already in use'),
 *    }),
 *  });
 */
Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  'checkEmailAvailability',
  checkEmailAvailability
);
